/**
 * The evidence ladder.
 *
 * A project's presentation tier is DERIVED from what is actually in its
 * `images` array — never authored per project. Adding a screenshot to
 * projects.json promotes a project automatically; no design decision is
 * required, and the system scales to 25 projects with zero extra work.
 *
 *   T1 CAPTURE  images contains "-web-"     a 16:10 live-site plate
 *   T2 SHOTS    images non-empty, no -web-  a scroll-snap strip at native aspect
 *   T3 ARTEFACT no images, markShape free   the bare mark on a bloom of its own colour
 *   T4 PLATE    no images, tile or mono     a gradient panel in the project's own colours
 *
 * T3 and T4 are the designed case, not the degraded one: every record
 * carries a real `gradient`, so an asset-poor project renders as something
 * specific to it rather than as a grey box.
 */

const req = require.context("@/assets/images", true, /\.(png|jpe?g|webp|svg)$/);

/** Resolve an "@/assets/..." path to a webpack URL, tolerating a miss. */
export function assetUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//.test(path)) return path;
  const rel = path.replace(/^@\/assets\/images\//, "./");
  try {
    return req(rel);
  } catch (e) {
    try {
      return require(`@/assets/${path.replace(/^@\/assets\//, "")}`);
    } catch (e2) {
      return "";
    }
  }
}

export function tierOf(project) {
  const images = project.images || [];
  if (images.some((i) => i.includes("-web-"))) return "T1";
  if (images.length) return "T2";
  return project.markShape === "free" ? "T3" : "T4";
}

export function captures(project) {
  return (project.images || []).filter((i) => i.includes("-web-"));
}

export function shots(project) {
  return (project.images || []).filter((i) => !i.includes("-web-"));
}

/** The project's own gradient as a CSS value. Always defined. */
export function gradientOf(project, angle = 135) {
  const g = project.gradient || {};
  const from = g.from || "#b4522d";
  const via = g.via || from;
  const to = g.to || via;
  return `linear-gradient(${angle}deg, ${from} 0%, ${via} 55%, ${to} 100%)`;
}

/** Colour-channel variables a section can bind once and reuse. */
export function colourVars(project) {
  const g = project.gradient || {};
  return {
    "--p-from": g.from || "#b4522d",
    "--p-via": g.via || g.from || "#b4522d",
    "--p-to": g.to || g.via || g.from || "#b4522d",
  };
}

export function useProjectAssets() {
  return { assetUrl, tierOf, captures, shots, gradientOf, colourVars };
}
